import { useEffect, useCallback } from 'react';
import { getSocket, disconnectSocket } from '../services/socket';

interface AutoLikerAction {
  id: number;
  configId: number;
  postId: string;
  status: string;
  error?: string;
  createdAt: string;
  vkData?: {
    postText: string;
    postUrl: string;
    authorName: string;
    authorPhoto: string;
    date: number;
  } | null;
}

interface AutoLikerStatus {
  configId: number;
  todayLikes: number;
  isActive: boolean;
}

interface AutoLikerError {
  configId: number;
  error: string;
}

export interface AutoLikerProgress {
  configId: number;
  phase: 'searching' | 'processing' | 'waiting' | 'completed' | 'error';
  message: string;
  details?: {
    postsFound?: number;
    postsProcessed?: number;
    postsSkipped?: number;
    skippedReasons?: { reason: string; count: number }[];
    nextRunIn?: number;
    // For phase === 'waiting': server-side absolute deadline (epoch ms) and
    // the initial duration (ms). The UI runs a local 1Hz countdown to display
    // "Ожидание N сек…" without any extra WebSocket traffic.
    waitUntilMs?: number;
    waitMs?: number;
  };
}

interface UseAutoLikerWebSocketProps {
  onAction?: (data: { configId: number; action: AutoLikerAction; vkData?: any }) => void;
  onStatus?: (data: AutoLikerStatus) => void;
  onError?: (data: AutoLikerError) => void;
  onProgress?: (data: AutoLikerProgress) => void;
}

export const useAutoLikerWebSocket = ({
  onAction,
  onStatus,
  onError,
  onProgress,
}: UseAutoLikerWebSocketProps) => {
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    if (onAction) socket.on('autoLiker:action', onAction);
    if (onStatus) socket.on('autoLiker:status', onStatus);
    if (onError) socket.on('autoLiker:error', onError);
    if (onProgress) socket.on('autoLiker:progress', onProgress);

    return () => {
      if (onAction) socket.off('autoLiker:action', onAction);
      if (onStatus) socket.off('autoLiker:status', onStatus);
      if (onError) socket.off('autoLiker:error', onError);
      if (onProgress) socket.off('autoLiker:progress', onProgress);
    };
  }, [onAction, onStatus, onError, onProgress]);

  const disconnect = useCallback(() => {
    disconnectSocket();
  }, []);

  return { disconnect };
};
